import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ServerIcon,
  CircleStackIcon,
  CloudIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';

interface HealthData {
  overall: string;
  services: Record<string, { status: string; message?: string; latency?: number }>;
  timestamp: string;
}

interface Statistics {
  totals: {
    users: number;
    farms: number;
    analyses: number;
    alerts: number;
    reports: number;
  };
  recentActivity: {
    analyses: { date: string; count: number }[];
  };
}

export default function SystemMonitoring() {
  const [health, setHealth] = useState<HealthData | null>(null);
  const [stats, setStats] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [healthRes, statsRes] = await Promise.all([
        axios.get(`${baseUrl}/api/v1/admin/health`, { headers }),
        axios.get(`${baseUrl}/api/v1/admin/statistics`, { headers }),
      ]);
      setHealth(healthRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch monitoring data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => fetchData(), 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
      case 'reachable':
        return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />;
      default:
        return <XCircleIcon className="h-6 w-6 text-red-500" />;
    }
  };

  const getServiceIcon = (name: string) => {
    switch (name) {
      case 'database':
        return <CircleStackIcon className="h-5 w-5" />;
      case 'redis':
        return <CpuChipIcon className="h-5 w-5" />;
      case 'sentinel_hub':
      case 'open_meteo':
        return <CloudIcon className="h-5 w-5" />;
      default:
        return <ServerIcon className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-64"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Monitoring</h1>
          <p className="text-gray-500 mt-1">Real-time system health and statistics</p>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Overall Status */}
      <div
        className={`rounded-lg p-6 ${
          health?.overall === 'healthy'
            ? 'bg-green-50 border border-green-200'
            : 'bg-yellow-50 border border-yellow-200'
        }`}
      >
        <div className="flex items-center">
          {health?.overall === 'healthy' ? (
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          ) : (
            <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
          )}
          <div className="ml-4">
            <h2 className="text-lg font-semibold text-gray-900">
              System Status: {health?.overall === 'healthy' ? 'All Systems Operational' : 'Degraded Performance'}
            </h2>
            <p className="text-sm text-gray-600">
              Last checked: {health?.timestamp ? new Date(health.timestamp).toLocaleString() : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(health?.services || {}).map(([name, service]) => (
          <div
            key={name}
            className={`bg-white rounded-lg shadow-sm p-4 border-l-4 ${
              service.status === 'healthy'
                ? 'border-green-500'
                : service.status === 'reachable'
                ? 'border-yellow-500'
                : 'border-red-500'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 rounded-lg mr-3">
                  {getServiceIcon(name)}
                </div>
                <span className="font-medium text-gray-900 capitalize">
                  {name.replace(/_/g, ' ')}
                </span>
              </div>
              {getStatusIcon(service.status)}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span
                className={`px-2 py-0.5 rounded-full ${
                  service.status === 'healthy'
                    ? 'bg-green-100 text-green-800'
                    : service.status === 'reachable'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {service.status}
              </span>
              {service.latency && (
                <span className="text-gray-500">{service.latency}ms</span>
              )}
            </div>
            {service.message && (
              <p className="mt-2 text-xs text-gray-500 truncate">{service.message}</p>
            )}
          </div>
        ))}
      </div>

      {/* Statistics */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Database Statistics</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Users', value: stats?.totals.users || 0 },
            { label: 'Farms', value: stats?.totals.farms || 0 },
            { label: 'Analyses', value: stats?.totals.analyses || 0 },
            { label: 'Alerts', value: stats?.totals.alerts || 0 },
            { label: 'Reports', value: stats?.totals.reports || 0 },
          ].map((stat) => (
            <div key={stat.label} className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{stat.value.toLocaleString()}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity Chart Placeholder */}
      {stats?.recentActivity.analyses && stats.recentActivity.analyses.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Analysis Activity (Last 7 Days)</h2>
          <div className="flex items-end justify-between h-32 px-4">
            {stats.recentActivity.analyses.map((day, index) => {
              const maxCount = Math.max(...stats.recentActivity.analyses.map((d) => d.count));
              const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
              return (
                <div key={index} className="flex flex-col items-center flex-1 mx-1">
                  <div
                    className="w-full bg-green-500 rounded-t"
                    style={{ height: `${Math.max(height, 4)}%` }}
                  ></div>
                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                  </p>
                  <p className="text-xs font-medium text-gray-700">{day.count}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
